import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useForm, Controller, type UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { ZodIssue } from 'zod'
import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import { ConfirmCancelModal } from '@/shared/components/ConfirmCancelModal'
import { ConfirmPositiveModal } from '@/shared/components/confirmation'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast/ToastProvider'
import { isAbortError, isNetworkApiError } from '@/shared/lib/apiErrorMessages'
import { ApiError } from '@/api/http/types'
import { checkWizardAiReadiness } from '../api/campaign-wizard-readiness'
import { messageFromCampaignAiStepError } from '../lib/mapApiUnknownError'
import { useCampaign } from '../api/campaign-queries'
import { useCampaignSetupCompanyOptions } from '../lib/useCampaignSetupCompanyOptions'
import {
  useCompleteStep1,
  useCreateCampaign,
  useDeleteCampaign,
  usePatchCampaign,
} from '../api/campaign-mutations'
import {
  campaignStep1FormSchema,
  campaignStep1SubmitSchema,
  parseOptionalNumber,
  todayDateOnlyLocal,
  type CampaignStep1FormValues,
  type CampaignStep1SubmitValues,
} from '../lib/campaign-schema'
import type { Campaign } from '../types'
import { CampaignWizardChrome } from '../components/CampaignWizardChrome'
import { CampaignCalendarPickerIcon } from '../components/CampaignCalendarPickerIcon'
import { CampaignGeneratingDialog } from '../components/CampaignGeneratingDialog'
import { CampaignSetupCompanyField } from '../components/CampaignSetupCompanyField'
import { useSessionStore } from '@/stores/useSessionStore'
import { queryKeys } from '@/api/query-keys'
import { WIZARD_ACTION_BUTTON_LAYOUT_SX, WIZARD_CANCEL_TEXT_BUTTON_SX } from '@/shared/lib/wizardActionButtons'
import campaignFieldVerifiedTickUrl from '@/assets/checkbox-circle-line-tick.svg'

dayjs.extend(utc)
dayjs.extend(customParseFormat)

const FORM_DATE_FMT = 'YYYY-MM-DD'

/** Default outlined field look — does not override `.Mui-focused` (focus ring unchanged). */
const CAMPAIGN_SETUP_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#FFF',
  },
  '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E5E7EB',
  },
  '& .MuiOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline':
    {
      borderColor: '#E5E7EB',
    },
} as const

/** Multiline field that grows to fill remaining form height above the action footer. */
const CAMPAIGN_SETUP_EXPANDABLE_TEXTAREA_SX = {
  ...CAMPAIGN_SETUP_FIELD_SX,
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  '& .MuiInputBase-root': {
    flex: 1,
    alignItems: 'stretch',
    minHeight: 96,
  },
  '& .MuiInputBase-inputMultiline': {
    flex: 1,
    minHeight: '100% !important',
    height: '100% !important',
    overflow: 'auto !important',
    boxSizing: 'border-box',
    resize: 'none',
  },
} as const

const CAMPAIGN_DATE_PICKER_SLOTS = {
  openPickerIcon: CampaignCalendarPickerIcon,
}

const BUDGET_FOCUS_HINT =
  'This is an estimate and not a final value. You can always set up an instrument-level budget in later stages.'

const TARGET_AUDIENCE_FOCUS_HINT_LINE1 =
  'Add your desired audience profile here. Describe the type of audience you want to reach. Ex: Age, Gender, Location, etc'
const TARGET_AUDIENCE_FOCUS_HINT_LINE2 =
  'You can define more detailed audience criteria and segmentation later when publishing an instrument.'

function utcDayFromFormString(s: string | undefined): Dayjs | null {
  if (!s) return null
  const d = dayjs.utc(s, FORM_DATE_FMT, true)
  return d.isValid() ? d : null
}

function utcDayStringFromPickerValue(v: Dayjs | null): string {
  if (!v?.isValid()) return ''
  return v.utc().startOf('day').format(FORM_DATE_FMT)
}

function isAbandonableShell(c: Campaign | undefined): boolean {
  if (!c) return false
  const hasName = c.name.trim().length > 0
  const hasGoal = c.researchGoal.trim().length > 0
  const hasAudience = c.targetAudience.trim().length > 0
  const hasDates = c.startDate != null || c.endDate != null
  const hasBudget = c.budget != null
  return !hasName && !hasGoal && !hasAudience && !hasDates && !hasBudget
}

const EPOCH_DATE_PREFIX = '1970-01-01'

function safeDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const day = iso.slice(0, 10)
  // Guard against epoch-0 stored by earlier bug (empty string coerced to Date(0))
  return day === EPOCH_DATE_PREFIX ? '' : day
}

function campaignToFormDefaults(c: Campaign): CampaignStep1FormValues {
  return {
    name: c.name ?? '',
    researchGoal: c.researchGoal ?? '',
    budget: c.budget ?? undefined,
    startDate: safeDate(c.startDate),
    endDate: safeDate(c.endDate),
    targetAudience: c.targetAudience ?? '',
    companyId: c.companyId ?? '',
  }
}

function isCompanyAssociationLocked(
  isNew: boolean,
  companyId: string | null | undefined,
): boolean {
  return !isNew && Boolean(companyId)
}

type ToastFn = (message: string, variant: 'success' | 'error') => void

function applyStep1SubmitErrors(
  form: UseFormReturn<CampaignStep1FormValues>,
  issues: { path: (string | number)[]; message: string }[],
) {
  const appliedPath = new Set<string>()
  for (const err of issues) {
    const key = err.path[0]
    if (typeof key !== 'string') continue
    if (appliedPath.has(key)) continue
    appliedPath.add(key)
    form.setError(key as keyof CampaignStep1FormValues, {
      message: err.message,
    })
  }
}

const STEP1_FOCUS_ORDER: (keyof CampaignStep1FormValues)[] = [
  'name',
  'researchGoal',
  'startDate',
  'endDate',
  'targetAudience',
]

function scrollToFirstStep1Issue(
  form: UseFormReturn<CampaignStep1FormValues>,
  issues: ZodIssue[],
) {
  for (const key of STEP1_FOCUS_ORDER) {
    if (issues.some((i) => i.path[0] === key)) {
      requestAnimationFrame(() => {
        Promise.resolve(form.setFocus(key)).catch(() => undefined)
        const el = document.querySelector(`[name="${String(key)}"]`)
        if (typeof el?.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
      return
    }
  }
}

function handleCampaignSetupApiError(
  e: unknown,
  form: UseFormReturn<CampaignStep1FormValues>,
  showToast: ToastFn,
) {
  if (!(e instanceof ApiError)) {
    showToast('Something went wrong. Please try again.', 'error')
    return
  }
  if (
    e.code === 'service_unavailable' ||
    e.code === 'network_error' ||
    e.code === 'unauthorized'
  ) {
    showToast(messageFromCampaignAiStepError(e), 'error')
    return
  }
  if (e.code === 'conflict') {
    form.setError('name', { message: e.message })
    showToast(e.message, 'error')
    return
  }
  if (e.code === 'validation_error') {
    showToast(
      e.message || 'Something went wrong. Please try again.',
      'error',
    )
    return
  }
  showToast(e.message || 'Something went wrong. Please try again.', 'error')
}

function trimStep1Payload(v: CampaignStep1SubmitValues) {
  return {
    name: v.name.trim(),
    researchGoal: v.researchGoal.trim(),
    budget: v.budget ?? null,
    startDate: v.startDate,
    endDate: v.endDate,
    targetAudience: v.targetAudience.trim(),
  }
}

function campaignNameFieldProgress(nameVal: string | undefined) {
  const t = nameVal?.trim() ?? ''
  if (t.length === 0) return { hint: null as string | null, ok: false }
  if (t.length < 10) {
    return { hint: 'Campaign name must be at least 10 characters long', ok: false }
  }
  if (t.length > 100) {
    return { hint: 'Campaign name must not exceed 100 characters', ok: false }
  }
  return { hint: null, ok: true }
}

function campaignGoalFieldProgress(goalVal: string | undefined) {
  const t = goalVal?.trim() ?? ''
  if (t.length === 0) {
    return { hint: null as string | null, guidance: null as string | null, ok: false }
  }
  if (t.length < 100) {
    return {
      hint: 'Research goal must be at least 100 characters long.',
      guidance: 'A clear research goal includes specific objectives, and measurable outcomes',
      ok: false,
    }
  }
  if (t.length > 2500) {
    return { hint: 'Research goal must not exceed 2500 characters.', guidance: null, ok: false }
  }
  return { hint: null, guidance: null, ok: true }
}

async function runCampaignSetupSaveDraft(params: {
  form: UseFormReturn<CampaignStep1FormValues>
  isNew: boolean
  campaignId: string
  sessionTenantId: string | null | undefined
  createCampaign: ReturnType<typeof useCreateCampaign>
  patchCampaign: ReturnType<typeof usePatchCampaign>
  queryClient: ReturnType<typeof useQueryClient>
  setDraftOpen: (open: boolean) => void
  showToast: ToastFn
  navigate: ReturnType<typeof useNavigate>
}): Promise<void> {
  const v = params.form.getValues()
  try {
    if (params.isNew) {
      const created = await params.createCampaign.mutateAsync({
        name: v.name.trim(),
        researchGoal: v.researchGoal.trim(),
        budget: parseOptionalNumber(v.budget) ?? null,
        startDate: v.startDate || null,
        endDate: v.endDate || null,
        targetAudience: v.targetAudience.trim(),
        status: 'inactive',
        wizardStep: 1,
        companyId: v.companyId?.trim() || null,
      })
      params.queryClient.setQueryData(
        queryKeys.campaigns.detail(params.sessionTenantId ?? '_', created.id),
        created,
      )
      params.setDraftOpen(false)
      params.showToast('Campaign saved as draft successfully', 'success')
      params.navigate(`/campaigns/${created.id}/setup`, { replace: true })
      return
    }
    await params.patchCampaign.mutateAsync({
      campaignId: params.campaignId,
      body: {
        name: v.name.trim(),
        researchGoal: v.researchGoal.trim(),
        budget: parseOptionalNumber(v.budget) ?? null,
        startDate: v.startDate || null,
        endDate: v.endDate || null,
        targetAudience: v.targetAudience.trim(),
        status: 'inactive',
        wizardStep: 1,
      },
    })
    params.setDraftOpen(false)
    params.showToast('Campaign saved as draft successfully', 'success')
  } catch {
    params.showToast('Something went wrong. Please try again.', 'error')
  }
}

async function runCampaignSetupCancel(params: {
  isNew: boolean
  campaign: Campaign | undefined
  campaignId: string
  deleteCampaign: ReturnType<typeof useDeleteCampaign>
  setCancelOpen: (open: boolean) => void
  navigate: ReturnType<typeof useNavigate>
}): Promise<void> {
  if (params.isNew) {
    params.setCancelOpen(false)
    params.navigate('/')
    return
  }
  if (params.campaign && isAbandonableShell(params.campaign)) {
    await params.deleteCampaign.mutateAsync(params.campaignId).catch(() => undefined)
  }
  params.setCancelOpen(false)
  params.navigate('/')
}

function renderCampaignSetupAccessGate(params: {
  role: string | undefined
  isNew: boolean
  isLoading: boolean
  isError: boolean
  campaign: Campaign | undefined
  campaignId: string
  refetch: () => void
}): ReactNode | null {
  if (params.role !== 'ADMIN') {
    return <Navigate to="/unauthorized" replace />
  }
  if (!params.isNew && params.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }
  if (!params.isNew && (params.isError || !params.campaign)) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error" gutterBottom>
          Could not load campaign.
        </Typography>
        <Button onClick={() => params.refetch()} sx={WIZARD_ACTION_BUTTON_LAYOUT_SX}>
          Retry
        </Button>
      </Box>
    )
  }
  if (!params.isNew && params.campaign?.companyStatus === 'inactive') {
    return <Navigate to={`/campaigns/${params.campaignId}`} replace />
  }
  return null
}

async function runCampaignSetupStep1Submit(params: {
  raw: CampaignStep1FormValues
  form: UseFormReturn<CampaignStep1FormValues>
  isNew: boolean
  campaignId: string
  companyIdVal: string | undefined
  createCampaign: ReturnType<typeof useCreateCampaign>
  completeStep1: ReturnType<typeof useCompleteStep1>
  setAnalyzeOpen: (open: boolean) => void
  analyzeAbortRef: MutableRefObject<AbortController | null>
  analyzeGenerationFailureHandledRef: MutableRefObject<boolean>
  failAnalyzeGeneration: (e: unknown) => void
  showToast: ToastFn
  navigate: ReturnType<typeof useNavigate>
}): Promise<void> {
  const parsed = campaignStep1SubmitSchema.safeParse(params.raw)
  if (!parsed.success) {
    applyStep1SubmitErrors(params.form, parsed.error.issues)
    scrollToFirstStep1Issue(params.form, parsed.error.issues)
    return
  }
  const payload = trimStep1Payload(parsed.data)

  params.analyzeGenerationFailureHandledRef.current = false
  params.analyzeAbortRef.current?.abort()
  const controller = new AbortController()
  params.analyzeAbortRef.current = controller
  const signal = controller.signal

  try {
    params.setAnalyzeOpen(true)
    await checkWizardAiReadiness(signal)
    if (params.isNew) {
      const created = await params.createCampaign.mutateAsync({
        ...payload,
        status: 'draft',
        wizardStep: 1,
        companyId: params.companyIdVal?.trim() || null,
        signal,
      })
      await params.completeStep1.mutateAsync({
        campaignId: created.id,
        body: payload,
        signal,
      })
      params.analyzeAbortRef.current = null
      params.showToast('Campaign saved successfully', 'success')
      params.navigate(`/campaigns/${created.id}/goal-analysis`, { replace: true })
      return
    }
    await params.completeStep1.mutateAsync({
      campaignId: params.campaignId,
      body: payload,
      signal,
    })
    params.analyzeAbortRef.current = null
    params.showToast('Campaign saved successfully', 'success')
    params.navigate(`/campaigns/${params.campaignId}/goal-analysis`, { replace: true })
  } catch (e) {
    if (params.analyzeGenerationFailureHandledRef.current || isAbortError(e)) return
    if (import.meta.env.DEV) {
      console.error('[campaign-wizard] step 1 analyze failed', e)
    }
    if (e instanceof ApiError) {
      handleCampaignSetupApiError(e, params.form, params.showToast)
      return
    }
    if (isNetworkApiError(e)) {
      params.failAnalyzeGeneration(e)
      return
    }
    handleCampaignSetupApiError(e, params.form, params.showToast)
  } finally {
    params.setAnalyzeOpen(false)
  }
}

export function CampaignSetupPage() {
  const role = useSessionStore((s) => s.user?.role)
  const sessionTenantId = useSessionStore((s) => s.user?.tenantId)
  const queryClient = useQueryClient()
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const [searchParams] = useSearchParams()
  const queryCompanyId = searchParams.get('companyId')?.trim() || ''
  const isNew = campaignId === 'new'
  useDocumentTitle('Campaign setup')
  const navigate = useNavigate()
  const { showToast } = useToast()

  const { data: campaign, isLoading, isError, refetch } = useCampaign(campaignId)
  const createCampaign = useCreateCampaign()
  const patchCampaign = usePatchCampaign()
  const completeStep1 = useCompleteStep1()
  const deleteCampaign = useDeleteCampaign()

  const form = useForm<CampaignStep1FormValues>({
    resolver: zodResolver(campaignStep1FormSchema),
    defaultValues: {
      name: '',
      researchGoal: '',
      budget: undefined,
      startDate: '',
      endDate: '',
      targetAudience: '',
      companyId: queryCompanyId,
    },
    mode: 'onChange',
  })

  const { watch, handleSubmit, reset, control, formState } = form
  const nameVal = watch('name')
  const goalVal = watch('researchGoal')
  const startDateVal = watch('startDate')
  const audienceVal = watch('targetAudience')
  const companyIdVal = watch('companyId')

  const companyLocked = isCompanyAssociationLocked(isNew, campaign?.companyId)
  const {
    options: companySelectOptions,
    locked: companyFieldLocked,
    fallbackName: companyFallbackName,
  } = useCampaignSetupCompanyOptions(companyIdVal, companyLocked)

  const [cancelOpen, setCancelOpen] = useState(false)
  const [draftOpen, setDraftOpen] = useState(false)
  const [analyzeOpen, setAnalyzeOpen] = useState(false)
  const [budgetFocused, setBudgetFocused] = useState(false)
  const [targetAudienceFocused, setTargetAudienceFocused] = useState(false)
  const analyzeGenerationFailureHandledRef = useRef(false)
  const analyzeAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!isNew && campaign) {
      reset(campaignToFormDefaults(campaign))
    }
  }, [campaign, reset, isNew])

  const failAnalyzeGeneration = useCallback(
    (e?: unknown) => {
      if (analyzeGenerationFailureHandledRef.current) return
      analyzeGenerationFailureHandledRef.current = true
      analyzeAbortRef.current?.abort()
      analyzeAbortRef.current = null
      setAnalyzeOpen(false)
      showToast(messageFromCampaignAiStepError(e), 'error')
    },
    [showToast],
  )

  useEffect(() => {
    if (!analyzeOpen) return
    const onOffline = () => {
      failAnalyzeGeneration(
        new Error(
          import.meta.env.DEV
            ? 'Browser went offline (check DevTools → Network → Offline is unchecked)'
            : 'Network connection lost during analysis',
        ),
      )
    }
    globalThis.addEventListener('offline', onOffline)
    return () => globalThis.removeEventListener('offline', onOffline)
  }, [analyzeOpen, failAnalyzeGeneration])

  const nameLen = nameVal?.length ?? 0
  const goalLen = goalVal?.length ?? 0

  const nameProgress = useMemo(() => campaignNameFieldProgress(nameVal), [nameVal])
  const goalProgress = useMemo(() => campaignGoalFieldProgress(goalVal), [goalVal])

  // Date-only "today" for picker minDate — local calendar, aligned with `todayDateOnlyLocal` in submit schema.
  const todayStr = todayDateOnlyLocal()
  const todayUtc = useMemo(
    () => dayjs.utc(todayStr, FORM_DATE_FMT, true).startOf('day'),
    [todayStr],
  )
  const endDateMinUtc = useMemo(() => {
    const start = utcDayFromFormString(startDateVal ?? '')
    if (!start) return todayUtc
    return start.isBefore(todayUtc) ? todayUtc : start
  }, [startDateVal, todayUtc])

  const saveDraftEnabled =
    (nameVal?.trim().length ?? 0) > 0 && (goalVal?.trim().length ?? 0) > 0

  const onSubmitNext = (raw: CampaignStep1FormValues) =>
    runCampaignSetupStep1Submit({
      raw,
      form,
      isNew,
      campaignId,
      companyIdVal,
      createCampaign,
      completeStep1,
      setAnalyzeOpen,
      analyzeAbortRef,
      analyzeGenerationFailureHandledRef,
      failAnalyzeGeneration,
      showToast,
      navigate,
    })

  const confirmSaveDraft = () =>
    runCampaignSetupSaveDraft({
      form,
      isNew,
      campaignId,
      sessionTenantId,
      createCampaign,
      patchCampaign,
      queryClient,
      setDraftOpen,
      showToast,
      navigate,
    })

  const confirmCancel = () =>
    runCampaignSetupCancel({
      isNew,
      campaign,
      campaignId,
      deleteCampaign,
      setCancelOpen,
      navigate,
    })

  const openCancelDialog = () => {
    setCancelOpen(true)
  }

  const accessGate = renderCampaignSetupAccessGate({
    role,
    isNew,
    isLoading,
    isError,
    campaign,
    campaignId,
    refetch: () => {
      Promise.resolve(refetch()).catch(() => undefined)
    },
  })
  if (accessGate) return accessGate

  return (
    <CampaignWizardChrome
      wizardStep={isNew ? 1 : (campaign?.wizardStep ?? 1)}
      totalWizardSteps={4}
      activePhaseIndex={0}
      campaignId={isNew ? undefined : campaignId}
    >
      <Box
        component="form"
        onSubmit={(event) => {
          void handleSubmit(onSubmitNext)(event)
        }}
        data-testid="campaign-setup-form"
        sx={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="h5" fontWeight={700} color="#23262f" gutterBottom>
            Campaign Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Define your campaign details.
          </Typography>
        </Box>

        <Stack
          spacing={2.5}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '& > *:not(:last-child)': { flexShrink: 0 },
          }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
              <Typography component="label" variant="body2" fontWeight={500}>
                Campaign Name
              </Typography>
              <Typography component="span" color="error">
                *
              </Typography>
            </Stack>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Enter campaign name"
                  error={!!formState.errors.name}
                  helperText={formState.errors.name?.message}
                  sx={CAMPAIGN_SETUP_FIELD_SX}
                  inputProps={{ maxLength: 100 }}
                  InputProps={{
                    endAdornment: nameProgress.ok ? (
                      <InputAdornment position="end">
                        <Box
                          component="img"
                          src={campaignFieldVerifiedTickUrl}
                          alt=""
                          aria-hidden
                          sx={{ width: 24, height: 24, display: 'block' }}
                        />
                      </InputAdornment>
                    ) : null,
                  }}
                />
              )}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mt: 0.5 }}
            >
              {nameProgress.hint ? (
                <Typography variant="caption" sx={{ color: 'info.main' }}>
                  {nameProgress.hint}
                </Typography>
              ) : (
                <span />
              )}
              <Typography variant="caption" color="text.secondary">
                {nameLen} / 100
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
              <Typography component="label" variant="body2" fontWeight={500}>
                Research Goal
              </Typography>
              <Typography component="span" color="error">
                *
              </Typography>
            </Stack>
            <Controller
              name="researchGoal"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Describe your primary and secondary research objectives in detail"
                  error={!!formState.errors.researchGoal}
                  helperText={formState.errors.researchGoal?.message}
                  sx={CAMPAIGN_SETUP_FIELD_SX}
                  inputProps={{ maxLength: 2500 }}
                  InputProps={{
                    endAdornment: goalProgress.ok ? (
                      <InputAdornment
                        position="end"
                        sx={{ alignSelf: 'flex-start', mt: '10px' }}
                      >
                        <Box
                          component="img"
                          src={campaignFieldVerifiedTickUrl}
                          alt=""
                          aria-hidden
                          sx={{ width: 24, height: 24, display: 'block' }}
                        />
                      </InputAdornment>
                    ) : null,
                  }}
                />
              )}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mt: 0.5 }}
            >
              {goalProgress.hint || goalProgress.guidance ? (
                <Stack direction="column" spacing={0.25}>
                  {goalProgress.guidance && (
                    <Typography variant="caption" sx={{ color: 'info.main' }}>
                      {goalProgress.guidance}
                    </Typography>
                  )}
                  {goalProgress.hint && (
                    <Typography variant="caption" sx={{ color: 'info.main' }}>
                      {goalProgress.hint}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <span />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {goalLen} / 2500
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Estimated Budget
            </Typography>
            <Controller
              name="budget"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  type="number"
                  name={field.name}
                  inputRef={field.ref}
                  placeholder="Enter budget amount"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    field.onChange(val === '' ? undefined : Number(val))
                  }}
                  onFocus={() => setBudgetFocused(true)}
                  onBlur={() => {
                    setBudgetFocused(false)
                    field.onBlur()
                  }}
                  sx={CAMPAIGN_SETUP_FIELD_SX}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              )}
            />
            {budgetFocused ? (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mt: 0.5 }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'info.main' }}
                  data-testid="campaign-setup-budget-focus-hint"
                >
                  {BUDGET_FOCUS_HINT}
                </Typography>
              </Stack>
            ) : null}
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box flex={1}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                  <Typography component="label" variant="body2" fontWeight={500}>
                    Start Date
                  </Typography>
                  <Typography component="span" color="error">
                    *
                  </Typography>
                </Stack>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      format={FORM_DATE_FMT}
                      value={utcDayFromFormString(field.value)}
                      onChange={(v) => field.onChange(utcDayStringFromPickerValue(v))}
                      minDate={todayUtc}
                      desktopModeMediaQuery="@media (min-width: 0px)"
                      slots={CAMPAIGN_DATE_PICKER_SLOTS}
                      slotProps={{
                        openPickerIcon: {
                          sx: { fontSize: 22, color: '#4a5565' },
                        },
                        textField: {
                          fullWidth: true,
                          name: field.name,
                          inputRef: field.ref,
                          onBlur: field.onBlur,
                          placeholder: 'Select start date',
                          error: !!formState.errors.startDate,
                          helperText: formState.errors.startDate?.message,
                          sx: CAMPAIGN_SETUP_FIELD_SX,
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <Box flex={1}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                  <Typography component="label" variant="body2" fontWeight={500}>
                    End Date
                  </Typography>
                  <Typography component="span" color="error">
                    *
                  </Typography>
                </Stack>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      format={FORM_DATE_FMT}
                      value={utcDayFromFormString(field.value)}
                      onChange={(v) => field.onChange(utcDayStringFromPickerValue(v))}
                      minDate={endDateMinUtc}
                      desktopModeMediaQuery="@media (min-width: 0px)"
                      slots={CAMPAIGN_DATE_PICKER_SLOTS}
                      slotProps={{
                        openPickerIcon: {
                          sx: { fontSize: 22, color: '#4a5565' },
                        },
                        textField: {
                          fullWidth: true,
                          name: field.name,
                          inputRef: field.ref,
                          onBlur: field.onBlur,
                          placeholder: 'Select end date',
                          error: !!formState.errors.endDate,
                          helperText: formState.errors.endDate?.message,
                          sx: CAMPAIGN_SETUP_FIELD_SX,
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Stack>
          </LocalizationProvider>

          <Box
            data-testid="campaign-setup-target-audience-field"
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1, flexShrink: 0 }}>
              <Typography component="label" variant="body2" fontWeight={500}>
                Target Audience
              </Typography>
              <Typography component="span" color="error">
                *
              </Typography>
            </Stack>
            <Controller
              name="targetAudience"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Describe your target audience"
                  error={!!formState.errors.targetAudience}
                  helperText={formState.errors.targetAudience?.message}
                  onFocus={() => setTargetAudienceFocused(true)}
                  onBlur={() => {
                    setTargetAudienceFocused(false)
                    field.onBlur()
                  }}
                  sx={CAMPAIGN_SETUP_EXPANDABLE_TEXTAREA_SX}
                />
              )}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mt: 0.5, flexShrink: 0 }}
            >
              {targetAudienceFocused && !formState.errors.targetAudience ? (
                <Box data-testid="campaign-setup-target-audience-focus-hint" sx={{ flex: 1, pr: 2 }}>
                  <Typography variant="caption" sx={{ color: 'info.main', display: 'block' }}>
                    {TARGET_AUDIENCE_FOCUS_HINT_LINE1}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'info.main', display: 'block' }}>
                    {TARGET_AUDIENCE_FOCUS_HINT_LINE2}
                  </Typography>
                </Box>
              ) : (
                <span />
              )}
              <Typography variant="caption" color="text.secondary">
                {(audienceVal?.length ?? 0)} / 1500
              </Typography>
            </Stack>
          </Box>

          {/* BFP-1010 — optional Company (screenshot: below Target Audience) */}
          <CampaignSetupCompanyField
            control={control}
            options={companySelectOptions}
            locked={companyFieldLocked}
            fallbackName={companyFallbackName}
          />
        </Stack>

        <Stack
          data-testid="campaign-setup-actions"
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mt: 'auto', flexShrink: 0, pt: 4, width: '100%' }}
        >
          <Button
            variant="text"
            color="inherit"
            onClick={openCancelDialog}
            sx={WIZARD_CANCEL_TEXT_BUTTON_SX}
          >
            Cancel
          </Button>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              data-testid="campaign-setup-save-draft"
              disabled={
                !saveDraftEnabled ||
                patchCampaign.isPending ||
                createCampaign.isPending
              }
              onClick={() => setDraftOpen(true)}
              sx={{
                ...WIZARD_ACTION_BUTTON_LAYOUT_SX,
                borderColor: saveDraftEnabled ? 'primary.main' : undefined,
                color: saveDraftEnabled ? 'primary.main' : undefined,
              }}
            >
              Save as Draft
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                analyzeOpen ||
                patchCampaign.isPending ||
                completeStep1.isPending ||
                createCampaign.isPending
              }
              sx={WIZARD_ACTION_BUTTON_LAYOUT_SX}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Box>

      <ConfirmCancelModal
        open={cancelOpen}
        onStay={() => setCancelOpen(false)}
        onConfirm={() => void confirmCancel()}
        dialogTestId="campaign-setup-cancel-dialog"
      />

      <ConfirmPositiveModal
        open={draftOpen}
        iconVariant="info"
        headline="Save as draft?"
        bodyLines={[
          'Your campaign will be saved so you can finish setup later.',
          'You can reopen it from Campaigns whenever you are ready.',
        ]}
        dismissLabel="Keep editing"
        confirmLabel="Save draft"
        onDismiss={() => setDraftOpen(false)}
        onConfirm={() => void confirmSaveDraft()}
        dialogTestId="campaign-setup-draft-dialog"
      />

      <CampaignGeneratingDialog
        open={analyzeOpen}
        subtitle="Generating Research objective"
        dataTestId="analyzing-research-dialog"
      />
    </CampaignWizardChrome>
  )
}
